import type { EntityChecker } from '@app/entities'
import { DashboardTemplate } from '@app/pages/Dashboard/GridPage/DrawerAddDashboard/types'
import { WidgetCommonFormFieldName } from '@app/pages/Dashboard/WidgetAddPage/types'
import { filterFalsies } from '@libs/filterFalsies'
import { isDefined } from '@libs/isDefined'
import type {
  MutationCreateDashboardWidget,
  MutationSetDashboardWidgetOptions
} from '@server/graphql/mutations/dashboard'
import {
  mutationCreateDashboardWidget,
  mutationSetDashboardWidgetOptions
} from '@server/graphql/mutations/dashboard'
import type {
  CreateDashboardWidgetMutationArgs,
  InputCreateDashboard,
  InputSetDashboardWidgetOptions,
  SetDashboardWidgetOptionsMutationArgs
} from '@server/graphql/typeDefs/types'
import {
  Criticity,
  DashboardWidgetDataOptionType,
  DashboardWidgetType
} from '@server/graphql/typeDefs/types'
import type { StoreRoot } from '..'
import type StoreWidgetsManagement from './StoreWidgetsManagement'
import type { IWidgetAttributes, IWidgetCoordinates } from './types'

export const getComplianceBarChartWidget = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  args: CreateDashboardWidgetMutationArgs,
  dashboardId: number
): Promise<void> => {
  return storeRoot
    .getGQLRequestor(storeWidgetsManagement.options.instanceName)
    .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
    .then(dataDashboardWidget => {
      const { id: dashboardWidgetId } =
        dataDashboardWidget.createDashboardWidget

      const { storeIoE } = storeWidgetsManagement.storeRoot.stores

      const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
        instanceName: storeWidgetsManagement.instanceName,
        dashboardId,
        dashboardWidgetId,
        type: DashboardWidgetType.BarChart,
        series: [
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: Array.from(storeIoE.checkers.values()).map(checker =>
                checker.getPropertyAsNumber('id')
              ),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              duration: 0
            },
            displayOptions: {
              type: DashboardWidgetType.BarChart,
              label: 'Total'
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(storeIoE.checkers, Criticity.Critical)
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              duration: 0
            },
            displayOptions: {
              type: DashboardWidgetType.BarChart,
              label: 'Critical'
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(storeIoE.checkers, Criticity.High)
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              duration: 0
            },
            displayOptions: {
              type: DashboardWidgetType.BarChart,
              label: 'High'
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(storeIoE.checkers, Criticity.Medium)
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              duration: 0
            },
            displayOptions: {
              type: DashboardWidgetType.BarChart,
              label: 'Medium'
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(storeIoE.checkers, Criticity.Low)
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              duration: 0
            },
            displayOptions: {
              type: DashboardWidgetType.BarChart,
              label: 'Low'
            }
          }
        ]
      }

      if (!dashboardWidgetOptions) {
        throw new Error('Missing widget options')
      }

      const args: SetDashboardWidgetOptionsMutationArgs = {
        dashboardWidgetOptions
      }

      return storeRoot
        .getGQLRequestor(storeWidgetsManagement.options.instanceName)
        .query<MutationSetDashboardWidgetOptions>(
          mutationSetDashboardWidgetOptions,
          args
        )
    })
    .then(results => {
      if (!results.setDashboardWidgetOptions) {
        throw new Error('An error has occurred when creating the widget')
      }

      storeWidgetsManagement.reset()

      storeRoot.stores.storeMessages.success(
        storeWidgetsManagement.translate('Widget added'),
        {
          labelledBy: 'widgetAdded'
        }
      )

      storeWidgetsManagement.storeFlags.success()
    })
}

export const getComplianceLineChartWidget = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  args: CreateDashboardWidgetMutationArgs,
  dashboardId: number
): Promise<void> => {
  return storeRoot
    .getGQLRequestor(storeWidgetsManagement.options.instanceName)
    .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
    .then(dataDashboardWidget => {
      const { id: dashboardWidgetId } =
        dataDashboardWidget.createDashboardWidget

      const { storeIoE } = storeRoot.stores

      const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
        instanceName: storeWidgetsManagement.instanceName,
        dashboardId,
        dashboardWidgetId,
        type: DashboardWidgetType.LineChart,
        series: [
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: Array.from(storeIoE.checkers.values()).map(checker =>
                checker.getPropertyAsNumber('id')
              ),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              // 1 month duration
              duration: 2592000
            },
            displayOptions: {
              type: DashboardWidgetType.LineChart,
              label: 'Total'
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(storeIoE.checkers, Criticity.Critical)
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              // 1 month duration
              duration: 2592000
            },
            displayOptions: {
              type: DashboardWidgetType.LineChart,
              label: 'Critical'
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(storeIoE.checkers, Criticity.High)
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              // 1 month duration
              duration: 2592000
            },
            displayOptions: {
              type: DashboardWidgetType.LineChart,
              label: 'High'
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(storeIoE.checkers, Criticity.Medium)
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              // 1 month duration
              duration: 2592000
            },
            displayOptions: {
              type: DashboardWidgetType.LineChart,
              label: 'Medium'
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(storeIoE.checkers, Criticity.Low)
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              // 1 month duration
              duration: 2592000
            },
            displayOptions: {
              type: DashboardWidgetType.LineChart,
              label: 'Low'
            }
          }
        ]
      }

      if (!dashboardWidgetOptions) {
        throw new Error('Missing widget options')
      }

      const args: SetDashboardWidgetOptionsMutationArgs = {
        dashboardWidgetOptions
      }

      return storeRoot
        .getGQLRequestor(storeWidgetsManagement.options.instanceName)
        .query<MutationSetDashboardWidgetOptions>(
          mutationSetDashboardWidgetOptions,
          args
        )
    })
    .then(results => {
      if (!results.setDashboardWidgetOptions) {
        throw new Error('An error has occurred when creating the widget')
      }

      storeWidgetsManagement.reset()

      storeRoot.stores.storeMessages.success(
        storeWidgetsManagement.translate('Widget added'),
        {
          labelledBy: 'widgetAdded'
        }
      )

      storeWidgetsManagement.storeFlags.success()
    })
}

export const getComplianceTotalScoreNumberWidget = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  args: CreateDashboardWidgetMutationArgs,
  dashboardId: number,
  checkerCodeNames: string[] = [] // if empty, it selects all the checkers
): Promise<void> => {
  return storeRoot
    .getGQLRequestor(storeWidgetsManagement.options.instanceName)
    .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
    .then(dataDashboardWidget => {
      const { id: dashboardWidgetId } =
        dataDashboardWidget.createDashboardWidget

      const { storeIoE } = storeRoot.stores

      const checkerIds =
        checkerCodeNames.length === 0
          ? Array.from(storeIoE.checkers.values()).map(checker =>
              checker.getPropertyAsNumber('id')
            )
          : checkerCodeNames
              .map(codeName =>
                storeRoot.stores.storeIoE.checkers
                  .get(codeName)
                  ?.getPropertyAsNumber('id')
              )
              .filter(isDefined)

      const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
        instanceName: storeWidgetsManagement.instanceName,
        dashboardId,
        dashboardWidgetId,
        type: DashboardWidgetType.BigNumber,
        series: [
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: checkerIds,
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              duration: 0
            },
            displayOptions: {
              type: DashboardWidgetType.BigNumber
            }
          }
        ]
      }

      if (!dashboardWidgetOptions) {
        throw new Error('Missing widget options')
      }

      const args: SetDashboardWidgetOptionsMutationArgs = {
        dashboardWidgetOptions
      }

      return storeRoot
        .getGQLRequestor(storeWidgetsManagement.options.instanceName)
        .query<MutationSetDashboardWidgetOptions>(
          mutationSetDashboardWidgetOptions,
          args
        )
    })
    .then(results => {
      if (!results.setDashboardWidgetOptions) {
        throw new Error('An error has occurred when creating the widget')
      }

      storeWidgetsManagement.reset()

      storeRoot.stores.storeMessages.success(
        storeWidgetsManagement.translate('Widget added'),
        {
          labelledBy: 'widgetAdded'
        }
      )

      storeWidgetsManagement.storeFlags.success()
    })
}

export const getDeviancesCounterPerCriticity = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  dashboardId: number,
  widgetCoordinates: IWidgetCoordinates,
  dashboard: InputCreateDashboard
): Array<Promise<void>> => {
  const criticitiesCounter = [
    {
      criticity: Criticity.Critical,
      title: 'Critical deviance count'
    },
    {
      criticity: Criticity.High,
      title: 'High deviance count'
    },
    {
      criticity: Criticity.Medium,
      title: 'Medium deviance count'
    },
    {
      criticity: Criticity.Low,
      title: 'Low deviance count'
    }
  ]

  const positionsX = [0, 2, 4, 6]
  const positionY = 4
  let counterX = 0

  const widgetsCounterCriticities = criticitiesCounter.map(criticityCounter => {
    widgetCoordinates.posX = positionsX[counterX]
    widgetCoordinates.posY = positionY

    counterX++

    const dashboardWidget =
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        widgetCoordinates,
        storeWidgetsManagement.translate(criticityCounter.title),
        dashboard.instanceName,
        dashboardId
      )

    const args: CreateDashboardWidgetMutationArgs = {
      dashboardWidget
    }

    const { storeIoE } = storeRoot.stores

    return storeRoot
      .getGQLRequestor(storeWidgetsManagement.options.instanceName)
      .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
      .then(dataDashboardWidget => {
        const { id: dashboardWidgetId } =
          dataDashboardWidget.createDashboardWidget

        const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
          instanceName: storeWidgetsManagement.instanceName,
          dashboardId,
          dashboardWidgetId,
          type: DashboardWidgetType.BigNumber,
          series: [
            {
              dataOptionsDeviance: {
                type: DashboardWidgetDataOptionType.Deviance,
                checkerIds: storeIoE
                  .getCheckersPerCriticity(
                    storeIoE.checkers,
                    criticityCounter.criticity
                  )
                  .map(checker => checker.getPropertyAsNumber('id')),
                directoryIds: [],
                // not yet implemented in API
                reasonIds: [],
                // FIXME Use real profileId when supported in API
                profileId: 1,
                duration: 0
              },
              displayOptions: {
                type: DashboardWidgetType.BigNumber
              }
            }
          ]
        }

        if (!dashboardWidgetOptions) {
          throw new Error('Missing widget options')
        }

        const args: SetDashboardWidgetOptionsMutationArgs = {
          dashboardWidgetOptions
        }

        return storeRoot
          .getGQLRequestor(storeWidgetsManagement.options.instanceName)
          .query<MutationSetDashboardWidgetOptions>(
            mutationSetDashboardWidgetOptions,
            args
          )
      })
      .then(results => {
        if (!results.setDashboardWidgetOptions) {
          throw new Error('An error has occurred when creating the widget')
        }

        storeWidgetsManagement.reset()

        storeRoot.stores.storeMessages.success(
          storeWidgetsManagement.translate('Widget added'),
          {
            labelledBy: 'widgetAdded'
          }
        )

        storeWidgetsManagement.storeFlags.success()
      })
  })

  return widgetsCounterCriticities
}

export const getDeviancesCounterPerRisk = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  dashboardId: number,
  widgetCoordinates: IWidgetCoordinates,
  dashboard: InputCreateDashboard,
  template: DashboardTemplate
): Array<Promise<void>> => {
  const risksCodeNames =
    template === DashboardTemplate.adRisk360
      ? [
          'C-UNCONST-DELEG',
          'C-DC-ACCESS-CONSISTENCY',
          'C-PASSWORD-POLICY',
          'C-PASSWORD-NOT-REQUIRED',
          'C-DANG-PRIMGROUPID',
          'C-CLEARTEXT-PASSWORD',
          'C-SLEEPING-ACCOUNTS',
          'C-ADMINCOUNT-ACCOUNT-PROPS',
          'C-USERS-CAN-JOIN-COMPUTERS',
          'C-ROOTOBJECTS-SD-CONSISTENCY',
          'C-REVER-PWD-GPO'
        ]
      : [
          'C-NATIVE-ADM-GROUP-MEMBERS',
          'C-PROTECTED-USERS-GROUP-UNUSED',
          'C-ADMINCOUNT-ACCOUNT-PROPS',
          'C-ADM-ACC-USAGE',
          'C-LAPS-UNSECURE-CONFIG'
        ]

  const risks = filterFalsies(
    risksCodeNames.map(codeName => {
      const checker = storeRoot.stores.storeIoE.checkers.get(codeName)

      if (!checker) {
        return null
      }

      return {
        title: checker.getPropertyAsString('name'),
        checkerId: checker.getPropertyAsNumber('id')
      }
    })
  )

  const positionsX = [0, 2, 4, 6]
  const positionsY = [0, 2, 4]
  let counterX = 0
  let counterY = 0

  const widgetsCounterRisks = risks.map(risk => {
    widgetCoordinates.posX = positionsX[counterX]
    widgetCoordinates.posY = positionsY[counterY]

    // we want 4 items per line
    if (counterX === 3) {
      counterX = -1
      counterY++
    }

    counterX++

    const dashboardWidget =
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        widgetCoordinates,
        risk.title || '',
        dashboard.instanceName,
        dashboardId
      )

    const args: CreateDashboardWidgetMutationArgs = {
      dashboardWidget
    }

    return storeRoot
      .getGQLRequestor(storeWidgetsManagement.options.instanceName)
      .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
      .then(dataDashboardWidget => {
        const { id: dashboardWidgetId } =
          dataDashboardWidget.createDashboardWidget

        const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
          instanceName: storeWidgetsManagement.instanceName,
          dashboardId,
          dashboardWidgetId,
          type: DashboardWidgetType.BigNumber,
          series: [
            {
              dataOptionsDeviance: {
                type: DashboardWidgetDataOptionType.Deviance,
                checkerIds: [risk.checkerId],
                directoryIds:
                  storeWidgetsManagement.storeInfrastructures.getSelectedDirectoryIds(),
                // not yet implemented in API
                reasonIds: [],
                // FIXME Use real profileId when supported in API
                // https://github.com/AlsidOfficial/DSC-Cassiopeia/issues/2337
                profileId: 1,
                // same duration for all series
                duration:
                  storeWidgetsManagement.storeForm.getFieldValueAsNumber(
                    WidgetCommonFormFieldName.dataOptionsDuration
                  )
              },
              displayOptions: {
                type: DashboardWidgetType.BigNumber
              }
            }
          ]
        }

        if (!dashboardWidgetOptions) {
          throw new Error('Missing widget options')
        }

        const args: SetDashboardWidgetOptionsMutationArgs = {
          dashboardWidgetOptions
        }

        return storeRoot
          .getGQLRequestor(storeWidgetsManagement.options.instanceName)
          .query<MutationSetDashboardWidgetOptions>(
            mutationSetDashboardWidgetOptions,
            args
          )
      })
      .then(results => {
        if (!results.setDashboardWidgetOptions) {
          throw new Error('An error has occurred when creating the widget')
        }

        storeWidgetsManagement.reset()

        storeRoot.stores.storeMessages.success(
          storeWidgetsManagement.translate('Widget added'),
          {
            labelledBy: 'widgetAdded'
          }
        )

        storeWidgetsManagement.storeFlags.success()
      })
  })

  return widgetsCounterRisks
}

export const getPasswordDeviancesOnUserAccounts = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  dashboardId: number,
  args: CreateDashboardWidgetMutationArgs
): Promise<void> => {
  const lineChartCheckerCodenames = [
    'C-PASSWORD-NOT-REQUIRED', // Account with Possible Empty Password
    'C-USERS-REVER-PWDS', // Reversible Passwords
    'C-REVER-PWD-GPO', // Reversible Passwords in GPO
    'C-PASSWORD-DONT-EXPIRE', // Accounts With Never Expiring Passwords
    'C-USER-PASSWORD', // User Account Using Old Password
    'C-PASSWORD-POLICY', // Application of Weak Password Policies on Users
    'C-KRBTGT-PASSWORD', // Last Password Change on KRBTGT account
    'C-AAD-SSO-PASSWORD', // Last Change of the Microsoft Entra Seamless SSO Account Password
    'C-CLEARTEXT-PASSWORD' // Potential Clear-Text Password
  ]
  const lineChartCheckersMap = new Map<string, EntityChecker>()
  lineChartCheckerCodenames.forEach(codeName => {
    const checker = storeRoot.stores.storeIoE.checkers.get(codeName)
    if (checker) {
      lineChartCheckersMap.set(codeName, checker)
    }
  })
  return storeRoot
    .getGQLRequestor(storeWidgetsManagement.options.instanceName)
    .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
    .then(dataDashboardWidget => {
      const { id: dashboardWidgetId } =
        dataDashboardWidget.createDashboardWidget

      const { storeIoE } = storeRoot.stores

      const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
        instanceName: storeWidgetsManagement.options.instanceName,
        dashboardId,
        dashboardWidgetId,
        type: DashboardWidgetType.LineChart,
        series: [
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: Array.from(lineChartCheckersMap.values()).map(
                checker => checker.getPropertyAsNumber('id')
              ),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              // 1 month duration
              duration: 2592000
            },
            displayOptions: {
              type: DashboardWidgetType.LineChart,
              label: storeWidgetsManagement.translate('Total')
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(
                  lineChartCheckersMap,
                  Criticity.Critical
                )
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              // 1 month duration
              duration: 2592000
            },
            displayOptions: {
              type: DashboardWidgetType.LineChart,
              label: storeWidgetsManagement.translate('Critical')
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(lineChartCheckersMap, Criticity.High)
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              // 1 month duration
              duration: 2592000
            },
            displayOptions: {
              type: DashboardWidgetType.LineChart,
              label: storeWidgetsManagement.translate('High')
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(lineChartCheckersMap, Criticity.Medium)
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              // 1 month duration
              duration: 2592000
            },
            displayOptions: {
              type: DashboardWidgetType.LineChart,
              label: storeWidgetsManagement.translate('Medium')
            }
          },
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: storeIoE
                .getCheckersPerCriticity(lineChartCheckersMap, Criticity.Low)
                .map(checker => checker.getPropertyAsNumber('id')),
              directoryIds: [],
              // not yet implemented in API
              reasonIds: [],
              // FIXME Use real profileId when supported in API
              profileId: 1,
              // 1 month duration
              duration: 2592000
            },
            displayOptions: {
              type: DashboardWidgetType.LineChart,
              label: storeWidgetsManagement.translate('Low')
            }
          }
        ]
      }

      if (!dashboardWidgetOptions) {
        throw new Error('Missing widget options')
      }

      const args: SetDashboardWidgetOptionsMutationArgs = {
        dashboardWidgetOptions
      }

      return storeRoot
        .getGQLRequestor(storeWidgetsManagement.options.instanceName)
        .query<MutationSetDashboardWidgetOptions>(
          mutationSetDashboardWidgetOptions,
          args
        )
    })
    .then(results => {
      if (!results.setDashboardWidgetOptions) {
        throw new Error('An error has occurred when creating the widget')
      }

      storeWidgetsManagement.reset()

      storeRoot.stores.storeMessages.success(
        storeWidgetsManagement.translate('Widget added'),
        {
          labelledBy: 'widgetAdded'
        }
      )

      storeWidgetsManagement.storeFlags.success()
    })
}

export const getPasswordCounterWidgets = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  dashboardId: number,
  widgetCoordinates: IWidgetCoordinates,
  dashboard: InputCreateDashboard
): Array<Promise<void>> => {
  const counterWidgets: IWidgetAttributes[] = [
    {
      codeNames: ['C-PASSWORD-NOT-REQUIRED'],
      widgetName: storeWidgetsManagement.translate('Potential Empty Passwords')
    },
    {
      codeNames: ['C-REVER-PWD-GPO', 'C-USERS-REVER-PWDS'],
      widgetName: storeWidgetsManagement.translate('Reversible passwords')
    },
    {
      codeNames: ['C-PASSWORD-DONT-EXPIRE'],
      widgetName: storeWidgetsManagement.translate(
        'Accounts with non-expiring passwords'
      )
    },
    {
      codeNames: ['C-USER-PASSWORD'],
      widgetName: storeWidgetsManagement.translate(
        'User accounts with an old password'
      )
    },
    {
      codeNames: ['C-PASSWORD-HASHES-ANALYSIS'],
      widgetName: 'Detection of password weaknesses'
    }
  ]
  let posX = 0
  const widgetRisks = counterWidgets.map(widgetAttributes => {
    widgetCoordinates.posX = posX % 8
    widgetCoordinates.posY = 2 + 2 * Math.floor(posX / 8)
    posX += 2

    const dashboardWidget =
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        widgetCoordinates,
        widgetAttributes.widgetName,
        dashboard.instanceName,
        dashboardId
      )
    const args: CreateDashboardWidgetMutationArgs = {
      dashboardWidget
    }
    const checkerIds = filterFalsies(
      widgetAttributes.codeNames.map(codeName => {
        const checker = storeRoot.stores.storeIoE.checkers.get(codeName)
        if (!checker) {
          return
        }
        return checker.getPropertyAsNumber('id')
      })
    )
    return storeWidgetsManagement.storeRoot
      .getGQLRequestor(storeWidgetsManagement.options.instanceName)
      .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
      .then(dataDashboardWidget => {
        const { id: dashboardWidgetId } =
          dataDashboardWidget.createDashboardWidget

        const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
          instanceName: storeWidgetsManagement.instanceName,
          dashboardId,
          dashboardWidgetId,
          type: DashboardWidgetType.BigNumber,
          series: [
            {
              dataOptionsComplianceScore: {
                type: DashboardWidgetDataOptionType.Deviance,
                checkerIds: checkerIds,
                directoryIds: [],
                // not yet implemented in API
                reasonIds: [],
                // FIXME Use real profileId when supported in API
                profileId: 1,
                // 1 month duration
                duration: 2592000
              },
              displayOptions: {
                type: DashboardWidgetType.BigNumber,
                label: storeWidgetsManagement.translate(
                  widgetAttributes.widgetName
                )
              }
            }
          ]
        }

        if (!dashboardWidgetOptions) {
          throw new Error('Missing widget options')
        }

        const args: SetDashboardWidgetOptionsMutationArgs = {
          dashboardWidgetOptions
        }

        return storeRoot
          .getGQLRequestor(storeWidgetsManagement.options.instanceName)
          .query<MutationSetDashboardWidgetOptions>(
            mutationSetDashboardWidgetOptions,
            args
          )
      })
      .then(results => {
        if (!results.setDashboardWidgetOptions) {
          throw new Error('An error has occurred when creating the widget')
        }

        storeWidgetsManagement.reset()

        storeRoot.stores.storeMessages.success(
          storeWidgetsManagement.translate('Widget added'),
          {
            labelledBy: 'widgetAdded'
          }
        )

        storeWidgetsManagement.storeFlags.success()
      })
  })

  return widgetRisks
}

export const getUsersScoreNumberWidget = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  args: CreateDashboardWidgetMutationArgs,
  dashboardId: number,
  allUsers: boolean,
  active?: boolean
): Promise<void> => {
  return storeRoot
    .getGQLRequestor(storeWidgetsManagement.options.instanceName)
    .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
    .then(dataDashboardWidget => {
      const { id: dashboardWidgetId } =
        dataDashboardWidget.createDashboardWidget

      const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
        instanceName: storeWidgetsManagement.instanceName,
        dashboardId,
        dashboardWidgetId,
        type: DashboardWidgetType.BigNumber,
        series: [
          allUsers
            ? {
                dataOptionsUser: {
                  type: DashboardWidgetDataOptionType.User,
                  directoryIds: [],
                  duration: 0
                },
                displayOptions: {
                  type: DashboardWidgetType.BigNumber
                }
              }
            : {
                dataOptionsUser: {
                  type: DashboardWidgetDataOptionType.User,
                  directoryIds: [],
                  duration: 0,
                  active: active
                },
                displayOptions: {
                  type: DashboardWidgetType.BigNumber
                }
              }
        ]
      }

      if (!dashboardWidgetOptions) {
        throw new Error('Missing widget options')
      }

      const args: SetDashboardWidgetOptionsMutationArgs = {
        dashboardWidgetOptions
      }

      return storeRoot
        .getGQLRequestor(storeWidgetsManagement.options.instanceName)
        .query<MutationSetDashboardWidgetOptions>(
          mutationSetDashboardWidgetOptions,
          args
        )
    })
    .then(results => {
      if (!results.setDashboardWidgetOptions) {
        throw new Error('An error has occurred when creating the widget')
      }

      storeWidgetsManagement.reset()

      storeRoot.stores.storeMessages.success(
        storeWidgetsManagement.translate('Widget added'),
        {
          labelledBy: 'widgetAdded'
        }
      )

      storeWidgetsManagement.storeFlags.success()
    })
}

export const getDevianceNumberWidgetFromCheckers = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  args: CreateDashboardWidgetMutationArgs,
  dashboardId: number,
  checkerCodeNames: string[]
): Promise<void> => {
  const checkers = checkerCodeNames
    .map(codeName => storeRoot.stores.storeIoE.checkers.get(codeName))
    .filter(isDefined)

  if (checkers.length === 0) {
    return Promise.resolve()
  }

  return storeRoot
    .getGQLRequestor(storeWidgetsManagement.options.instanceName)
    .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
    .then(dataDashboardWidget => {
      const { id: dashboardWidgetId } =
        dataDashboardWidget.createDashboardWidget

      const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
        instanceName: storeWidgetsManagement.instanceName,
        dashboardId,
        dashboardWidgetId,
        type: DashboardWidgetType.BigNumber,
        series: [
          {
            dataOptionsDeviance: {
              type: DashboardWidgetDataOptionType.Deviance,
              checkerIds: checkers.map(checker =>
                checker.getPropertyAsNumber('id')
              ),
              directoryIds: [],
              reasonIds: [],
              profileId: 1,
              duration: 1296000
            },
            displayOptions: {
              type: DashboardWidgetType.BigNumber
            }
          }
        ]
      }

      if (!dashboardWidgetOptions) {
        throw new Error('Missing widget options')
      }

      const args: SetDashboardWidgetOptionsMutationArgs = {
        dashboardWidgetOptions
      }

      return storeRoot
        .getGQLRequestor(storeWidgetsManagement.options.instanceName)
        .query<MutationSetDashboardWidgetOptions>(
          mutationSetDashboardWidgetOptions,
          args
        )
    })
    .then(results => {
      if (!results.setDashboardWidgetOptions) {
        throw new Error('An error has occurred when creating the widget')
      }

      storeWidgetsManagement.reset()

      storeRoot.stores.storeMessages.success(
        storeWidgetsManagement.translate('Widget added'),
        {
          labelledBy: 'widgetAdded'
        }
      )

      storeWidgetsManagement.storeFlags.success()
    })
}

export const getUserEvolutionLineChartWidget = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  args: CreateDashboardWidgetMutationArgs,
  dashboardId: number,
  sleepingAccounts?: boolean
): Promise<void> => {
  return storeRoot
    .getGQLRequestor(storeWidgetsManagement.options.instanceName)
    .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
    .then(dataDashboardWidget => {
      const { id: dashboardWidgetId } =
        dataDashboardWidget.createDashboardWidget

      const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
        instanceName: storeWidgetsManagement.instanceName,
        dashboardId,
        dashboardWidgetId,
        type: DashboardWidgetType.LineChart,
        series: [
          sleepingAccounts
            ? {
                dataOptionsDeviance: {
                  type: DashboardWidgetDataOptionType.Deviance,
                  checkerIds: [
                    storeRoot.stores.storeIoE.checkers
                      .get('C-SLEEPING-ACCOUNTS')
                      ?.getPropertyAsNumber('id') ?? 0
                  ],
                  directoryIds: [],
                  reasonIds: [],
                  // 1 month duration
                  duration: 2592000,
                  profileId: 1
                },
                displayOptions: {
                  type: DashboardWidgetType.LineChart
                }
              }
            : {
                dataOptionsUser: {
                  type: DashboardWidgetDataOptionType.User,
                  directoryIds: [],
                  // 1 month duration
                  duration: 2592000
                },
                displayOptions: {
                  type: DashboardWidgetType.LineChart
                }
              }
        ]
      }

      if (!dashboardWidgetOptions) {
        throw new Error('Missing widget options')
      }

      const args: SetDashboardWidgetOptionsMutationArgs = {
        dashboardWidgetOptions
      }

      return storeRoot
        .getGQLRequestor(storeWidgetsManagement.options.instanceName)
        .query<MutationSetDashboardWidgetOptions>(
          mutationSetDashboardWidgetOptions,
          args
        )
    })
    .then(results => {
      if (!results.setDashboardWidgetOptions) {
        throw new Error('An error has occurred when creating the widget')
      }

      storeWidgetsManagement.reset()

      storeRoot.stores.storeMessages.success(
        storeWidgetsManagement.translate('Widget added'),
        {
          labelledBy: 'widgetAdded'
        }
      )

      storeWidgetsManagement.storeFlags.success()
    })
}

export const getDevianceLineChartWidget = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  args: CreateDashboardWidgetMutationArgs,
  dashboardId: number,
  label: string,
  checkerCodeNames: string[]
): Promise<void> => {
  return storeRoot
    .getGQLRequestor(storeWidgetsManagement.options.instanceName)
    .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
    .then(dataDashboardWidget => {
      const { id: dashboardWidgetId } =
        dataDashboardWidget.createDashboardWidget

      const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
        instanceName: storeWidgetsManagement.instanceName,
        dashboardId,
        dashboardWidgetId,
        type: DashboardWidgetType.LineChart,
        series: [
          {
            dataOptionsDeviance: {
              type: DashboardWidgetDataOptionType.Deviance,
              checkerIds: checkerCodeNames
                .map(checkerName =>
                  storeRoot.stores.storeIoE.checkers
                    .get(checkerName)
                    ?.getPropertyAsNumber('id')
                )
                .filter(isDefined),

              directoryIds: [],
              reasonIds: [],
              duration: 31536000,
              profileId: 1
            },
            displayOptions: {
              type: DashboardWidgetType.LineChart,
              label: label
            }
          }
        ]
      }

      if (!dashboardWidgetOptions) {
        throw new Error('Missing widget options')
      }

      const args: SetDashboardWidgetOptionsMutationArgs = {
        dashboardWidgetOptions
      }

      return storeRoot
        .getGQLRequestor(storeWidgetsManagement.options.instanceName)
        .query<MutationSetDashboardWidgetOptions>(
          mutationSetDashboardWidgetOptions,
          args
        )
    })
    .then(results => {
      if (!results.setDashboardWidgetOptions) {
        throw new Error('An error has occurred when creating the widget')
      }

      storeWidgetsManagement.reset()

      storeRoot.stores.storeMessages.success(
        storeWidgetsManagement.translate('Widget added'),
        {
          labelledBy: 'widgetAdded'
        }
      )

      storeWidgetsManagement.storeFlags.success()
    })
}

export const getAcscGlobalComplianceScoreByDomainBarChartWidget = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  args: CreateDashboardWidgetMutationArgs,
  dashboardId: number,
  checkerCodeNames: string[]
): Promise<void> => {
  const checkerIds = checkerCodeNames
    .map(checkerName =>
      storeRoot.stores.storeIoE.checkers
        .get(checkerName)
        ?.getPropertyAsNumber('id')
    )
    .filter(isDefined)
  return storeRoot
    .getGQLRequestor(storeWidgetsManagement.options.instanceName)
    .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
    .then(dataDashboardWidget => {
      const { id: dashboardWidgetId } =
        dataDashboardWidget.createDashboardWidget

      const domainWidgets = Array.from(
        storeRoot.stores.storeInfrastructures.directories.values()
      )
        .map(directory => {
          return directory.id
            ? {
                dataOptionsComplianceScore: {
                  type: DashboardWidgetDataOptionType.ComplianceScore,
                  checkerIds: checkerIds,
                  directoryIds: [directory.id],
                  reasonIds: [],
                  profileId: 1,
                  duration: 1296000
                },
                displayOptions: {
                  type: DashboardWidgetType.BarChart,
                  label: directory.name
                }
              }
            : undefined
        })
        .filter(isDefined)

      const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
        instanceName: storeWidgetsManagement.instanceName,
        dashboardId,
        dashboardWidgetId,
        type: DashboardWidgetType.BarChart,
        series: [
          {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: checkerIds,
              directoryIds: [],
              reasonIds: [],
              profileId: 1,
              duration: 1296000
            },
            displayOptions: {
              type: DashboardWidgetType.BarChart,
              label: '*All Domains*'
            }
          },
          ...domainWidgets
        ]
      }

      if (!dashboardWidgetOptions) {
        throw new Error('Missing widget options')
      }

      const args: SetDashboardWidgetOptionsMutationArgs = {
        dashboardWidgetOptions
      }

      return storeRoot
        .getGQLRequestor(storeWidgetsManagement.options.instanceName)
        .query<MutationSetDashboardWidgetOptions>(
          mutationSetDashboardWidgetOptions,
          args
        )
    })
    .then(results => {
      if (!results.setDashboardWidgetOptions) {
        throw new Error('An error has occurred when creating the widget')
      }

      storeWidgetsManagement.reset()

      storeRoot.stores.storeMessages.success(
        storeWidgetsManagement.translate('Widget added'),
        {
          labelledBy: 'widgetAdded'
        }
      )

      storeWidgetsManagement.storeFlags.success()
    })
}

export const getAcscGlobalComplianceScoreByTechniqueBarChartWidget = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  args: CreateDashboardWidgetMutationArgs,
  dashboardId: number,
  checkerCodeNames: string[]
): Promise<void> => {
  const keyToCheckerCodeNames = new Map([
    ['Kerberoasting', ['C-PRIV-ACCOUNTS-SPN']],
    ['ASrepRoasting', ['C-KERBEROS-CONFIG-ACCOUNT']],
    ['SkeletonKey', ['C-KERBEROS-CONFIG-ACCOUNT']],
    [
      'Password spraying',
      ['C-PASSWORD-NOT-REQUIRED', 'C-PASSWORD-HASHES-ANALYSIS']
    ],
    ['MachineAccountQuota', ['C-USERS-CAN-JOIN-COMPUTERS']],
    ['Unconstrained Delegation', ['C-UNCONST-DELEG']],
    ['Passwords found in GPO', ['C-REVER-PWD-GPO']],
    ['ADCS Compromise', ['C-PKI-DANG-ACCESS']],
    ['Golden Certificate', ['C-PKI-DANG-ACCESS']],
    ['DCSync', ['C-ROOTOBJECTS-SD-CONSISTENCY']],
    ['Dumping ntds.dit', ['C-NATIVE-ADM-GROUP-MEMBERS']],
    ['Golden Ticket', ['C-NATIVE-ADM-GROUP-MEMBERS', 'C-KRBTGT-PASSWORD']],
    ['Silver Ticket', ['C-NATIVE-ADM-GROUP-MEMBERS']],
    ['EntraConnect Compromise', ['C-AAD-CONNECT', 'C-AAD-SSO-PASSWORD']],
    ['1-way domain trust bypas', ['C-DANGEROUS-TRUST-RELATIONSHIP']],
    ['SidHistory Compromise', ['C-ACCOUNTS-DANG-SID-HISTORY']],
    ['Kerberoasting - Compliance', ['C-PRIV-ACCOUNTS-SPN']]
  ])
  const checkerIds = checkerCodeNames
    .map(checkerName =>
      storeRoot.stores.storeIoE.checkers
        .get(checkerName)
        ?.getPropertyAsNumber('id')
    )
    .filter(isDefined)
  return storeRoot
    .getGQLRequestor(storeWidgetsManagement.options.instanceName)
    .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
    .then(dataDashboardWidget => {
      const { id: dashboardWidgetId } =
        dataDashboardWidget.createDashboardWidget

      const techniqueWidgets = [...keyToCheckerCodeNames].map(
        ([key, codeNames]) => {
          return {
            dataOptionsComplianceScore: {
              type: DashboardWidgetDataOptionType.ComplianceScore,
              checkerIds: codeNames
                .map(checkerName =>
                  storeRoot.stores.storeIoE.checkers
                    .get(checkerName)
                    ?.getPropertyAsNumber('id')
                )
                .filter(isDefined),
              directoryIds: [],
              reasonIds: [],
              profileId: 1,
              duration: 1296000
            },
            displayOptions: {
              type: DashboardWidgetType.BarChart,
              label: key
            }
          }
        }
      )

      const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
        instanceName: storeWidgetsManagement.instanceName,
        dashboardId,
        dashboardWidgetId,
        type: DashboardWidgetType.BarChart,
        series: techniqueWidgets
      }

      if (!dashboardWidgetOptions) {
        throw new Error('Missing widget options')
      }

      const args: SetDashboardWidgetOptionsMutationArgs = {
        dashboardWidgetOptions
      }

      return storeRoot
        .getGQLRequestor(storeWidgetsManagement.options.instanceName)
        .query<MutationSetDashboardWidgetOptions>(
          mutationSetDashboardWidgetOptions,
          args
        )
    })
    .then(results => {
      if (!results.setDashboardWidgetOptions) {
        throw new Error('An error has occurred when creating the widget')
      }

      storeWidgetsManagement.reset()

      storeRoot.stores.storeMessages.success(
        storeWidgetsManagement.translate('Widget added'),
        {
          labelledBy: 'widgetAdded'
        }
      )

      storeWidgetsManagement.storeFlags.success()
    })
}

interface ISerieWidgetData {
  checkerCodeNames: string[]
  label: string
}
export const getDevianceMultipleSeriesLineChartWidget = (
  storeRoot: StoreRoot,
  storeWidgetsManagement: StoreWidgetsManagement,
  args: CreateDashboardWidgetMutationArgs,
  dashboardId: number,
  series: ISerieWidgetData[]
): Promise<void> => {
  return storeRoot
    .getGQLRequestor(storeWidgetsManagement.options.instanceName)
    .query<MutationCreateDashboardWidget>(mutationCreateDashboardWidget, args) // set options for the created widget
    .then(dataDashboardWidget => {
      const { id: dashboardWidgetId } =
        dataDashboardWidget.createDashboardWidget

      const widgetSeries = series.map(s => {
        return {
          dataOptionsDeviance: {
            type: DashboardWidgetDataOptionType.Deviance,
            checkerIds: s.checkerCodeNames
              .map(checkerName =>
                storeRoot.stores.storeIoE.checkers
                  .get(checkerName)
                  ?.getPropertyAsNumber('id')
              )
              .filter(isDefined),

            directoryIds: [],
            reasonIds: [],
            duration: 31536000,
            profileId: 1
          },
          displayOptions: {
            type: DashboardWidgetType.LineChart,
            label: s.label
          }
        }
      })
      const dashboardWidgetOptions: InputSetDashboardWidgetOptions = {
        instanceName: storeWidgetsManagement.instanceName,
        dashboardId,
        dashboardWidgetId,
        type: DashboardWidgetType.LineChart,
        series: widgetSeries
      }

      if (!dashboardWidgetOptions) {
        throw new Error('Missing widget options')
      }

      const args: SetDashboardWidgetOptionsMutationArgs = {
        dashboardWidgetOptions
      }

      return storeRoot
        .getGQLRequestor(storeWidgetsManagement.options.instanceName)
        .query<MutationSetDashboardWidgetOptions>(
          mutationSetDashboardWidgetOptions,
          args
        )
    })
    .then(results => {
      if (!results.setDashboardWidgetOptions) {
        throw new Error('An error has occurred when creating the widget')
      }

      storeWidgetsManagement.reset()

      storeRoot.stores.storeMessages.success(
        storeWidgetsManagement.translate('Widget added'),
        {
          labelledBy: 'widgetAdded'
        }
      )

      storeWidgetsManagement.storeFlags.success()
    })
}

export const getACSCTemplate = async (
  storeWidgetsManagement: StoreWidgetsManagement,
  dashboardId: number,
  dashboard: InputCreateDashboard
): Promise<void[]> => {
  const acscCheckerCodeNames = [
    'C-PRIV-ACCOUNTS-SPN',
    'C-UNCONST-DELEG',
    'C-KERBEROS-CONFIG-ACCOUNT',
    'C-DANGEROUS-TRUST-RELATIONSHIP',
    'C-NATIVE-ADM-GROUP-MEMBERS',
    'C-KRBTGT-PASSWORD',
    'C-REVER-PWD-GPO',
    'C-ACCOUNTS-DANG-SID-HISTORY',
    'C-ROOTOBJECTS-SD-CONSISTENCY',
    'C-GPO-SD-CONSISTENCY',
    'C-DC-ACCESS-CONSISTENCY',
    'C-AAD-CONNECT',
    'C-AAD-SSO-PASSWORD',
    'C-USERS-CAN-JOIN-COMPUTERS',
    'C-PASSWORD-NOT-REQUIRED',
    'C-PKI-DANG-ACCESS',
    'C-PASSWORD-HASHES-ANALYSIS'
  ]
  // ACSC COMPLIANCE SCORE
  const acscComplianceScore: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 0
        },
        storeWidgetsManagement.translate('ACSC Compliance Score'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const acscComplianceScoreNumberWidget = getComplianceTotalScoreNumberWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    acscComplianceScore,
    dashboardId,
    acscCheckerCodeNames
  )
  // ACSC VIOLATIONS
  const acscViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 0
        },
        storeWidgetsManagement.translate('ACSC Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const acscViolationsDevianceNumberWidget =
    getDevianceNumberWidgetFromCheckers(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      acscViolations,
      dashboardId,
      acscCheckerCodeNames
    )
  // ACSC GLOBAL COMPLIANCE - BY DOMAIN
  const acscGlobalComplianceByDomain: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 4,
          posY: 0
        },
        storeWidgetsManagement.translate('ACSC Global Compliance - by domain'),
        dashboard.instanceName,
        dashboardId,
        4
      )
  }
  const acscGlobalComplianceByDomainWidget =
    getAcscGlobalComplianceScoreByDomainBarChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      acscGlobalComplianceByDomain,
      dashboardId,
      acscCheckerCodeNames
    )
  // ACSC VIOLATION - REMEDIATION TRENDS
  const acscViolationRemediationTrends: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 2
        },
        storeWidgetsManagement.translate(
          'ACSC Violations - Remediation Trends'
        ),
        dashboard.instanceName,
        dashboardId,
        4
      )
  }
  const acscViolationRemediationTrendsLineChartWidget =
    getDevianceLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      acscViolationRemediationTrends,
      dashboardId,
      'Violations',
      acscCheckerCodeNames
    )
  // ACSC GLOBAL COMPLIANCE - BY TECHNIQUE
  const acscGlobalComplianceByTechnique: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 4,
          posY: 2
        },
        storeWidgetsManagement.translate(
          'ACSC Global Compliance - by technique'
        ),
        dashboard.instanceName,
        dashboardId,
        4
      )
  }
  const acscGlobalComplianceByTechniqueWidget =
    getAcscGlobalComplianceScoreByTechniqueBarChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      acscGlobalComplianceByTechnique,
      dashboardId,
      acscCheckerCodeNames
    )
  // KERBEROASTING - VIOLATIONS
  const kerberoastingViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 4
        },
        storeWidgetsManagement.translate('Kerberoasting - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const kerberoastingViolationsWidget = getDevianceNumberWidgetFromCheckers(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    kerberoastingViolations,
    dashboardId,
    ['C-PRIV-ACCOUNTS-SPN']
  )
  // KERBEROASTING COMPLIANCE
  const kerberoastingCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 4
        },
        storeWidgetsManagement.translate('Kerberoasting - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const kerberoastingComplianceWidget = getComplianceTotalScoreNumberWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    kerberoastingCompliance,
    dashboardId,
    ['C-PRIV-ACCOUNTS-SPN']
  )
  // KERBEROASTING - REMEDIATION TRENDS
  const kerberoastingRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 4
          },
          storeWidgetsManagement.translate(
            'Kerberoasting - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const kerberoastingRemediationTrendsLineChartWidget =
    getDevianceLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      kerberoastingRemediationTrendsLineChart,
      dashboardId,
      'Privileged accounts running kerberos services',
      ['C-PRIV-ACCOUNTS-SPN']
    )
  // ASREPROAST - VIOLATIONS
  const asrepRoastViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 6
        },
        storeWidgetsManagement.translate('ASRepRoast - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const asrepRoastViolationsWidget = getDevianceNumberWidgetFromCheckers(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    asrepRoastViolations,
    dashboardId,
    ['C-KERBEROS-CONFIG-ACCOUNT']
  )
  // ASREPROAST COMPLIANCE
  const asrepRoastCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 6
        },
        storeWidgetsManagement.translate('ASrepRoast - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const asrepRoastComplianceWidget = getComplianceTotalScoreNumberWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    asrepRoastCompliance,
    dashboardId,
    ['C-KERBEROS-CONFIG-ACCOUNT']
  )
  // ASREPROAST - REMEDIATION TRENDS
  const asrepRoastRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 6
          },
          storeWidgetsManagement.translate('ASRepRoast - Remediation Trends'),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const asrepRoastRemediationTrendsLineChartWidget = getDevianceLineChartWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    asrepRoastRemediationTrendsLineChart,
    dashboardId,
    'Kerberos configuration on user accounts',
    ['C-KERBEROS-CONFIG-ACCOUNT']
  )
  // PASSWORD SPRAYING - VIOLATIONS
  const passwordSprayingCheckerCodeNames = [
    'C-PASSWORD-NOT-REQUIRED',
    'C-PASSWORD-HASHES-ANALYSIS'
  ]
  const passwordSprayingViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 6
        },
        storeWidgetsManagement.translate('Password Spraying - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const passwordSprayingViolationsWidget = getDevianceNumberWidgetFromCheckers(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    passwordSprayingViolations,
    dashboardId,
    passwordSprayingCheckerCodeNames
  )
  // PASSWORD SPRAYING COMPLIANCE
  const passwordSprayingCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 8
        },
        storeWidgetsManagement.translate('Password spraying - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const passwordSprayingComplianceWidget = getComplianceTotalScoreNumberWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    passwordSprayingCompliance,
    dashboardId,
    passwordSprayingCheckerCodeNames
  )
  // PASSWORD SPRAYING - REMEDIATION TRENDS
  const passwordSprayingRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 8
          },
          storeWidgetsManagement.translate(
            'Password Spraying - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const passwordSprayingRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      passwordSprayingRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: ['C-PASSWORD-HASHES-ANALYSIS'],
          label: 'Detection of password weaknesses'
        },
        {
          checkerCodeNames: ['C-PASSWORD-NOT-REQUIRED'],
          label: 'Accounts with empty password'
        }
      ]
    )
  // MACHINEACCOUNTQUOTA - VIOLATIONS
  const machineAccountQuotaCheckerCodeNames = ['C-USERS-CAN-JOIN-COMPUTERS']
  const machineAccountQuotaViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 10
        },
        storeWidgetsManagement.translate('MachineAccountQuota - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const machineAccountQuotaViolationsWidget =
    getDevianceNumberWidgetFromCheckers(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      machineAccountQuotaViolations,
      dashboardId,
      machineAccountQuotaCheckerCodeNames
    )
  // MACHINEACCOUNTQUOTA COMPLIANCE
  const machineAccountQuotaCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 10
        },
        storeWidgetsManagement.translate('MachineAccountQuota - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const machineAccountQuotaComplianceWidget =
    getComplianceTotalScoreNumberWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      machineAccountQuotaCompliance,
      dashboardId,
      machineAccountQuotaCheckerCodeNames
    )
  // MACHINEACCOUNTQUOTA - REMEDIATION TRENDS
  const machineAccountQuotaRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 10
          },
          storeWidgetsManagement.translate(
            'MachineAccountQuota - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const machineAccountQuotaRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      machineAccountQuotaRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: machineAccountQuotaCheckerCodeNames,
          label: 'Users allowed to join computers'
        }
      ]
    )
  // UNCONSTRAINED DELEGATION - VIOLATIONS
  const unconstrainedDelegationCheckerCodeNames = ['C-UNCONST-DELEG']
  const unconstrainedDelegationViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 12
        },
        storeWidgetsManagement.translate(
          'Unconstrained Delegation - Violations'
        ),
        dashboard.instanceName,
        dashboardId
      )
  }
  const unconstrainedDelegationViolationsWidget =
    getDevianceNumberWidgetFromCheckers(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      unconstrainedDelegationViolations,
      dashboardId,
      unconstrainedDelegationCheckerCodeNames
    )
  // UNCONSTRAINED DELEGATION COMPLIANCE
  const unconstrainedDelegationCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 12
        },
        storeWidgetsManagement.translate(
          'Unconstrained Delegation - Compliance'
        ),
        dashboard.instanceName,
        dashboardId
      )
  }
  const unconstrainedDelegationComplianceWidget =
    getComplianceTotalScoreNumberWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      unconstrainedDelegationCompliance,
      dashboardId,
      unconstrainedDelegationCheckerCodeNames
    )
  // UNCONSTRAINED DELEGATION - REMEDIATION TRENDS
  const unconstrainedDelegationRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 12
          },
          storeWidgetsManagement.translate(
            'Unconstrained Delegation - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const unconstrainedDelegationRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      unconstrainedDelegationRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: unconstrainedDelegationCheckerCodeNames,
          label: 'Dangerous Kerberos Delegations'
        }
      ]
    )
  // PASSWORDS FOUND IN GPO - VIOLATIONS
  const passwordsFoundInGPOCheckerCodeNames = ['C-REVER-PWD-GPO']
  const passwordsFoundInGPOViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 14
        },
        storeWidgetsManagement.translate('Passwords found in GPO - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const passwordsFoundInGPOViolationsWidget =
    getDevianceNumberWidgetFromCheckers(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      passwordsFoundInGPOViolations,
      dashboardId,
      passwordsFoundInGPOCheckerCodeNames
    )
  // PASSWORDS FOUND IN GPO COMPLIANCE
  const passwordsFoundInGPOCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 14
        },
        storeWidgetsManagement.translate('Passwords found in GPO - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const passwordsFoundInGPOComplianceWidget =
    getComplianceTotalScoreNumberWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      passwordsFoundInGPOCompliance,
      dashboardId,
      passwordsFoundInGPOCheckerCodeNames
    )
  // PASSWORDS FOUND IN GPO - REMEDIATION TRENDS
  const passwordsFoundInGPORemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 14
          },
          storeWidgetsManagement.translate(
            'Passwords found in GPO - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const passwordsFoundInGPORemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      passwordsFoundInGPORemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: passwordsFoundInGPOCheckerCodeNames,
          label: 'Reversible passwords in GPO'
        }
      ]
    )
  // ADCS COMPROMISE - VIOLATIONS
  const adcsCompromiseCheckerCodeNames = ['C-PKI-DANG-ACCESS']
  const adcsCompromiseViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 16
        },
        storeWidgetsManagement.translate('ADCS Compromise - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const adcsCompromiseViolationsWidget = getDevianceNumberWidgetFromCheckers(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    adcsCompromiseViolations,
    dashboardId,
    adcsCompromiseCheckerCodeNames
  )
  // ADCS COMPROMISE COMPLIANCE
  const adcsCompromiseCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 16
        },
        storeWidgetsManagement.translate('ADCS Compromise - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const adcsCompromiseComplianceWidget = getComplianceTotalScoreNumberWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    adcsCompromiseCompliance,
    dashboardId,
    adcsCompromiseCheckerCodeNames
  )
  // ADCS COMPROMISE - REMEDIATION TRENDS
  const adcsCompromiseRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 16
          },
          storeWidgetsManagement.translate(
            'ADCS Compromise - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const adcsCompromiseRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      adcsCompromiseRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: adcsCompromiseCheckerCodeNames,
          label: 'ADCS Dangerous Misconfigurations'
        }
      ]
    )
  // GOLDEN CERTIFICATE - VIOLATIONS
  const goldenCertificateCheckerCodeNames = ['C-PKI-DANG-ACCESS']
  const goldenCertificateViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 18
        },
        storeWidgetsManagement.translate('ADCS Compromise - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const goldenCertificateViolationsWidget = getDevianceNumberWidgetFromCheckers(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    goldenCertificateViolations,
    dashboardId,
    goldenCertificateCheckerCodeNames
  )
  // GOLDEN CERTIFICATE COMPLIANCE
  const goldenCertificateCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 18
        },
        storeWidgetsManagement.translate('ADCS Compromise - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const goldenCertificateComplianceWidget = getComplianceTotalScoreNumberWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    goldenCertificateCompliance,
    dashboardId,
    goldenCertificateCheckerCodeNames
  )
  // GOLDEN CERTIFICATE - REMEDIATION TRENDS
  const goldenCertificateRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 18
          },
          storeWidgetsManagement.translate(
            'ADCS Compromise - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const goldenCertificateRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      goldenCertificateRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: goldenCertificateCheckerCodeNames,
          label: 'ADCS Dangerous Misconfigurations'
        }
      ]
    )
  // DCSYNC - VIOLATIONS
  const dcsyncCheckerCodeNames = ['C-ROOTOBJECTS-SD-CONSISTENCY']
  const dcsyncViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 20
        },
        storeWidgetsManagement.translate('DCSync - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const dcsyncViolationsWidget = getDevianceNumberWidgetFromCheckers(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    dcsyncViolations,
    dashboardId,
    dcsyncCheckerCodeNames
  )
  // DCSYNC COMPLIANCE
  const dcsyncCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 20
        },
        storeWidgetsManagement.translate('DCSync - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const dcsyncComplianceWidget = getComplianceTotalScoreNumberWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    dcsyncCompliance,
    dashboardId,
    dcsyncCheckerCodeNames
  )
  // DCSYNC - REMEDIATION TRENDS
  const dcsyncRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 4,
          posY: 20
        },
        storeWidgetsManagement.translate('DCSync - Remediation Trends'),
        dashboard.instanceName,
        dashboardId,
        4
      )
  }
  const dcsyncRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      dcsyncRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: dcsyncCheckerCodeNames,
          label: 'Root permissions allowing DCSync-like attacks'
        }
      ]
    )
  // DUMPING NTDS.DIT - VIOLATIONS
  const dumpingNtdsDitCheckerCodeNames = ['C-NATIVE-ADM-GROUP-MEMBERS']
  const dumpingNtdsDitViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 22
        },
        storeWidgetsManagement.translate('Dumping ntds.dit - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const dumpingNtdsDitViolationsWidget = getDevianceNumberWidgetFromCheckers(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    dumpingNtdsDitViolations,
    dashboardId,
    dumpingNtdsDitCheckerCodeNames
  )
  // DUMPING NTDS.DIT COMPLIANCE
  const dumpingNtdsDitCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 22
        },
        storeWidgetsManagement.translate('Dumping ntds.dit - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const dumpingNtdsDitComplianceWidget = getComplianceTotalScoreNumberWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    dumpingNtdsDitCompliance,
    dashboardId,
    dumpingNtdsDitCheckerCodeNames
  )
  // DUMPING NTDS.DIT - REMEDIATION TRENDS
  const dumpingNtdsDitRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 22
          },
          storeWidgetsManagement.translate(
            'Dumping ntds.dit - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const dumpingNtdsDitRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      dumpingNtdsDitRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: dumpingNtdsDitCheckerCodeNames,
          label: 'Native Administrative Group Members'
        }
      ]
    )
  // GOLDEN TICKET - VIOLATIONS
  const goldenTicketCheckerCodeNames = [
    'C-NATIVE-ADM-GROUP-MEMBERS',
    'C-KRBTGT-PASSWORD'
  ]
  const goldenTicketViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 24
        },
        storeWidgetsManagement.translate('Golden Ticket - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const goldenTicketViolationsWidget = getDevianceNumberWidgetFromCheckers(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    goldenTicketViolations,
    dashboardId,
    goldenTicketCheckerCodeNames
  )
  // GOLDEN TICKET COMPLIANCE
  const goldenTicketCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 24
        },
        storeWidgetsManagement.translate('Golden Ticket - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const goldenTicketComplianceWidget = getComplianceTotalScoreNumberWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    goldenTicketCompliance,
    dashboardId,
    goldenTicketCheckerCodeNames
  )
  // GOLDEN TICKET - REMEDIATION TRENDS
  const goldenTicketRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 24
          },
          storeWidgetsManagement.translate(
            'Golden Ticket - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const goldenTicketRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      goldenTicketRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: ['C-NATIVE-ADM-GROUP-MEMBERS'],
          label: 'Native Administrative Group Members'
        },
        {
          checkerCodeNames: ['C-KRBTGT-PASSWORD'],
          label: 'Last password change on KRBTGT account'
        }
      ]
    )
  // SILVER TICKET - VIOLATIONS
  const silverTicketCheckerCodeNames = ['C-NATIVE-ADM-GROUP-MEMBERS']
  const silverTicketViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 26
        },
        storeWidgetsManagement.translate('Silver Ticket - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const silverTicketViolationsWidget = getDevianceNumberWidgetFromCheckers(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    silverTicketViolations,
    dashboardId,
    silverTicketCheckerCodeNames
  )
  // SILVER TICKET COMPLIANCE
  const silverTicketCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 26
        },
        storeWidgetsManagement.translate('Silver Ticket - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const silverTicketComplianceWidget = getComplianceTotalScoreNumberWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    silverTicketCompliance,
    dashboardId,
    silverTicketCheckerCodeNames
  )
  // SILVER TICKET - REMEDIATION TRENDS
  const silverTicketRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 26
          },
          storeWidgetsManagement.translate(
            'Silver Ticket - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const silverTicketRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      silverTicketRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: silverTicketCheckerCodeNames,
          label: 'Native Administrative Group Members'
        }
      ]
    )
  // ENTRACONNECT COMPROMISE - VIOLATIONS
  const entraConnectCompromiseCheckerCodeNames = [
    'C-AAD-CONNECT',
    'C-AAD-SSO-PASSWORD'
  ]
  const entraConnectCompromiseViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 28
        },
        storeWidgetsManagement.translate(
          'EntraConnect Compromise - Violations'
        ),
        dashboard.instanceName,
        dashboardId
      )
  }
  const entraConnectCompromiseViolationsWidget =
    getDevianceNumberWidgetFromCheckers(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      entraConnectCompromiseViolations,
      dashboardId,
      entraConnectCompromiseCheckerCodeNames
    )
  // ENTRACONNECT COMPROMISE COMPLIANCE
  const entraConnectCompromiseCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 28
        },
        storeWidgetsManagement.translate(
          'EntraConnect Compromise - Compliance'
        ),
        dashboard.instanceName,
        dashboardId
      )
  }
  const entraConnectCompromiseComplianceWidget =
    getComplianceTotalScoreNumberWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      entraConnectCompromiseCompliance,
      dashboardId,
      entraConnectCompromiseCheckerCodeNames
    )
  // ENTRACONNECT COMPROMISE - REMEDIATION TRENDS
  const entraConnectCompromiseRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 28
          },
          storeWidgetsManagement.translate(
            'EntraConnect Compromise - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const entraConnectCompromiseRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      entraConnectCompromiseRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: ['C-AAD-CONNECT'],
          label: 'Verify Permissions Related to EntraConnect Account'
        },
        {
          checkerCodeNames: ['C-AAD-SSO-PASSWORD'],
          label: 'Last Change of the Entra Seamless SSO Account Password'
        }
      ]
    )
  // 1-WAY DOMAIN TRUST BYPASS - VIOLATIONS
  const oneWayDomainTrustBypassCheckerCodeNames = [
    'C-DANGEROUS-TRUST-RELATIONSHIP'
  ]
  const oneWayDomainTrustBypassViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 30
        },
        storeWidgetsManagement.translate(
          '1-way domain trust bypass - Violations'
        ),
        dashboard.instanceName,
        dashboardId
      )
  }
  const oneWayDomainTrustBypassViolationsWidget =
    getDevianceNumberWidgetFromCheckers(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      oneWayDomainTrustBypassViolations,
      dashboardId,
      oneWayDomainTrustBypassCheckerCodeNames
    )
  // 1-WAY DOMAIN TRUST BYPASS COMPLIANCE
  const oneWayDomainTrustBypassCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 30
        },
        storeWidgetsManagement.translate(
          '1-way domain trust bypass - Compliance'
        ),
        dashboard.instanceName,
        dashboardId
      )
  }
  const oneWayDomainTrustBypassComplianceWidget =
    getComplianceTotalScoreNumberWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      oneWayDomainTrustBypassCompliance,
      dashboardId,
      oneWayDomainTrustBypassCheckerCodeNames
    )
  // 1-WAY DOMAIN TRUST BYPASS - REMEDIATION TRENDS
  const oneWayDomainTrustBypassRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 30
          },
          storeWidgetsManagement.translate(
            '1-way domain trust bypass - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const oneWayDomainTrustBypassRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      oneWayDomainTrustBypassRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: oneWayDomainTrustBypassCheckerCodeNames,
          label: 'Dangerous Trust Relationships'
        }
      ]
    )
  // SIDHISTORY COMPROMISE - VIOLATIONS
  const sidHistoryCompromisCheckerCodeNames = ['C-ACCOUNTS-DANG-SID-HISTORY']
  const sidHistoryCompromisViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 32
        },
        storeWidgetsManagement.translate('SidHistory Compromise - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const sidHistoryCompromisViolationsWidget =
    getDevianceNumberWidgetFromCheckers(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      sidHistoryCompromisViolations,
      dashboardId,
      sidHistoryCompromisCheckerCodeNames
    )
  // SIDHISTORY COMPROMISE COMPLIANCE
  const sidHistoryCompromisCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 32
        },
        storeWidgetsManagement.translate('SidHistory Compromise - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const sidHistoryCompromisComplianceWidget =
    getComplianceTotalScoreNumberWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      sidHistoryCompromisCompliance,
      dashboardId,
      sidHistoryCompromisCheckerCodeNames
    )
  // SIDHISTORY COMPROMISE - REMEDIATION TRENDS
  const sidHistoryCompromisRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 32
          },
          storeWidgetsManagement.translate(
            'SidHistory Compromise - Remediation Trends'
          ),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const sidHistoryCompromisRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      sidHistoryCompromisRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: sidHistoryCompromisCheckerCodeNames,
          label: 'Accounts With a Dangerous SID History Attribute'
        }
      ]
    )
  // SKELETONKEY - VIOLATIONS
  const skeletonKeyCheckerCodeNames = [
    'C-NATIVE-ADM-GROUP-MEMBERS',
    'C-GPO-SD-CONSISTENCY',
    'C-DC-ACCESS-CONSISTENCY'
  ]
  const skeletonKeyViolations: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 0,
          posY: 34
        },
        storeWidgetsManagement.translate('SkeletonKey - Violations'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const skeletonKeyViolationsWidget = getDevianceNumberWidgetFromCheckers(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    skeletonKeyViolations,
    dashboardId,
    skeletonKeyCheckerCodeNames
  )
  // SKELETONKEY COMPLIANCE
  const skeletonKeyCompliance: CreateDashboardWidgetMutationArgs = {
    dashboardWidget:
      storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
        {
          posX: 2,
          posY: 34
        },
        storeWidgetsManagement.translate('SkeletonKey - Compliance'),
        dashboard.instanceName,
        dashboardId
      )
  }
  const skeletonKeyComplianceWidget = getComplianceTotalScoreNumberWidget(
    storeWidgetsManagement.storeRoot,
    storeWidgetsManagement,
    skeletonKeyCompliance,
    dashboardId,
    skeletonKeyCheckerCodeNames
  )
  // SKELETONKEY - REMEDIATION TRENDS
  const skeletonKeyRemediationTrendsLineChart: CreateDashboardWidgetMutationArgs =
    {
      dashboardWidget:
        storeWidgetsManagement.getDashboardWidgetVariablesForPredefinedCreation(
          {
            posX: 4,
            posY: 34
          },
          storeWidgetsManagement.translate('SkeletonKey - Remediation Trends'),
          dashboard.instanceName,
          dashboardId,
          4
        )
    }
  const skeletonKeyRemediationTrendsLineChartWidget =
    getDevianceMultipleSeriesLineChartWidget(
      storeWidgetsManagement.storeRoot,
      storeWidgetsManagement,
      skeletonKeyRemediationTrendsLineChart,
      dashboardId,
      [
        {
          checkerCodeNames: ['C-DC-ACCESS-CONSISTENCY'],
          label: 'DCs managed by illegitimate users'
        },
        {
          checkerCodeNames: ['C-NATIVE-ADM-GROUP-MEMBERS'],
          label: 'Native admin group members'
        },
        {
          checkerCodeNames: ['C-GPO-SD-CONSISTENCY'],
          label: 'sensitive GPO permissions'
        }
      ]
    )

  return Promise.all([
    acscComplianceScoreNumberWidget,
    acscViolationsDevianceNumberWidget,
    acscGlobalComplianceByDomainWidget,
    acscViolationRemediationTrendsLineChartWidget,
    acscGlobalComplianceByTechniqueWidget,
    kerberoastingViolationsWidget,
    kerberoastingComplianceWidget,
    kerberoastingRemediationTrendsLineChartWidget,
    asrepRoastViolationsWidget,
    asrepRoastComplianceWidget,
    asrepRoastRemediationTrendsLineChartWidget,
    passwordSprayingViolationsWidget,
    passwordSprayingComplianceWidget,
    passwordSprayingRemediationTrendsLineChartWidget,
    machineAccountQuotaViolationsWidget,
    machineAccountQuotaComplianceWidget,
    machineAccountQuotaRemediationTrendsLineChartWidget,
    unconstrainedDelegationViolationsWidget,
    unconstrainedDelegationComplianceWidget,
    unconstrainedDelegationRemediationTrendsLineChartWidget,
    passwordsFoundInGPOViolationsWidget,
    passwordsFoundInGPOComplianceWidget,
    passwordsFoundInGPORemediationTrendsLineChartWidget,
    adcsCompromiseViolationsWidget,
    adcsCompromiseComplianceWidget,
    adcsCompromiseRemediationTrendsLineChartWidget,
    goldenCertificateViolationsWidget,
    goldenCertificateComplianceWidget,
    goldenCertificateRemediationTrendsLineChartWidget,
    dcsyncViolationsWidget,
    dcsyncComplianceWidget,
    dcsyncRemediationTrendsLineChartWidget,
    dumpingNtdsDitViolationsWidget,
    dumpingNtdsDitComplianceWidget,
    dumpingNtdsDitRemediationTrendsLineChartWidget,
    goldenTicketViolationsWidget,
    goldenTicketComplianceWidget,
    goldenTicketRemediationTrendsLineChartWidget,
    silverTicketViolationsWidget,
    silverTicketComplianceWidget,
    silverTicketRemediationTrendsLineChartWidget,
    entraConnectCompromiseViolationsWidget,
    entraConnectCompromiseComplianceWidget,
    entraConnectCompromiseRemediationTrendsLineChartWidget,
    oneWayDomainTrustBypassViolationsWidget,
    oneWayDomainTrustBypassComplianceWidget,
    oneWayDomainTrustBypassRemediationTrendsLineChartWidget,
    sidHistoryCompromisViolationsWidget,
    sidHistoryCompromisComplianceWidget,
    sidHistoryCompromisRemediationTrendsLineChartWidget,
    skeletonKeyViolationsWidget,
    skeletonKeyComplianceWidget,
    skeletonKeyRemediationTrendsLineChartWidget
  ])
}
